// Calendar tab: Monday-first month grid of install spans, plus the month's agenda below it.
import type { Item } from '@shared/index';
import { STATUS_LABEL, addDays, compareQuotes, fmtDate, fmtShortDay, installDays, isDone, isoFromParts, weekday } from '@shared/index';
import { allStageItems, jobItems, store, today, ui } from '../store';
import { registerView, render } from '../render';
import { openJob } from '../nav';
import { $, el, pmColour, pmSeg } from '../ui';

export interface Cell { iso: string; day: number; out: boolean; we: boolean; today: boolean }
export interface Span { j: Item; days: string[] }

/** First and last ISO date of a month (m is 0-based; may overflow either way). */
export function monthBounds(y: number, m: number): { first: string; last: string } {
  const yy = y + Math.floor(m / 12), mm = ((m % 12) + 12) % 12;
  const first = isoFromParts(yy, mm + 1, 1);
  const next = mm === 11 ? isoFromParts(yy + 1, 1, 1) : isoFromParts(yy, mm + 2, 1);
  return { first, last: addDays(next, -1) };
}

/** Whole weeks, Monday to Sunday, that cover the month. */
export function monthCells(y: number, m: number, todayIso: string): Cell[] {
  const { first, last } = monthBounds(y, m);
  const start = addDays(first, -((weekday(first) + 6) % 7));
  const end = addDays(last, 6 - ((weekday(last) + 6) % 7));
  const cells: Cell[] = [];
  for (let iso = start; iso <= end; iso = addDays(iso, 1)) {
    const w = weekday(iso);
    cells.push({ iso, day: Number(iso.slice(8, 10)), out: iso < first || iso > last, we: w === 0 || w === 6, today: iso === todayIso });
  }
  return cells;
}

/** Items with an install date (and the PM filter), each with its weekday spans. */
export function installSpans(items: Item[], pm: string): Span[] {
  return items.filter((j) => j.install_date && (!pm || j.pm === pm)).map((j) => ({ j, days: installDays(j) }));
}

/** Items on each day, sorted by PM then quote. */
export function byDay(spans: Span[]): Map<string, Item[]> {
  const map = new Map<string, Item[]>();
  spans.forEach(({ j, days }) => days.forEach((d) => { if (!map.has(d)) map.set(d, []); map.get(d)!.push(j); }));
  map.forEach((L) => L.sort((a, b) => a.pm.localeCompare(b.pm) || compareQuotes(a.ref || a.quote, b.ref || b.quote) ||
    (((a as { sort_order?: number }).sort_order || 0) - ((b as { sort_order?: number }).sort_order || 0))));
  return map;
}

/** Spans that touch the month, earliest start first. */
export function agendaSpans(spans: Span[], first: string, last: string): Span[] {
  return spans.filter((s) => s.days.length && s.days[s.days.length - 1] >= first && s.days[0] <= last)
    .sort((a, b) => (a.days[0] < b.days[0] ? -1 : a.days[0] > b.days[0] ? 1 : 0));
}

const monthName = (iso: string, withYear: boolean) =>
  new Date(iso + 'T00:00:00Z').toLocaleDateString('en-AU', { month: 'long', ...(withYear ? { year: 'numeric' as const } : {}), timeZone: 'UTC' });

const craneBooked = (j: Item) => j.crane === 'Booked' || j.crane === 'Not needed';

function renderKey() {
  const key = $('pmKey'); key.replaceChildren();
  const add = (colour: string, label: string) => {
    const s = el('span'); const i = el('i'); i.style.background = colour; s.append(i, label); key.append(s);
  };
  store.pms.filter((p) => p.active).forEach((p) => add(pmColour(p.name), p.name));
  add('var(--bad)', 'Edge = crane not booked');
}

function renderCalendar() {
  pmSeg($('calPm'), ui.cal.pm, (pm) => { ui.cal.pm = pm; render(['calendar']); });
  renderKey();
  const { y, m } = ui.cal;
  const { first, last } = monthBounds(y, m);
  $('calTitle').textContent = monthName(first, true);
  const spans = store.loaded ? installSpans([...jobItems(), ...allStageItems()], ui.cal.pm) : [];
  const days = byDay(spans);
  const grid = $('calGrid'); grid.replaceChildren();
  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].forEach((d) => grid.append(el('div', 'dow', d)));
  monthCells(y, m, today()).forEach((c) => {
    const cell = el('div', 'day' + (c.out ? ' out' : '') + (c.we ? ' we' : '') + (c.today ? ' today' : ''));
    cell.append(el('span', 'dn', c.day));
    const evs = days.get(c.iso) || [];
    evs.slice(0, 3).forEach((j) => {
      const b = el('button', 'ev' + (isDone(j) ? ' done' : '') + (!craneBooked(j) && !isDone(j) ? ' nocrane' : ''), (j.ref || j.quote) + ' ' + j.address);
      b.type = 'button';
      b.style.background = pmColour(j.pm);
      b.title = j.quote + ' · ' + j.address + ' · ' + j.pm;
      b.addEventListener('click', () => openJob(j.quote));
      cell.append(b);
    });
    if (evs.length > 3) cell.append(el('span', 'more', '+' + (evs.length - 3) + ' more'));
    grid.append(cell);
  });

  const inMonth = agendaSpans(spans, first, last);
  const ag = $('agenda'); ag.replaceChildren();
  if (!store.loaded) { $('agTitle').textContent = 'Installs in ' + monthName(first, false); ag.append(el('div', 'empty', 'Loading…')); return; }
  $('agTitle').textContent = 'Installs in ' + monthName(first, false) + ' (' + inMonth.length + ')';
  if (!inMonth.length) ag.append(el('div', 'empty', 'No installs booked this month. Add an install date and days onsite to a job and it appears here.'));
  inMonth.forEach(({ j, days: d }) => {
    const row = el('div', 'ag');
    const w = el('div', 'when', fmtShortDay(d[0]));
    const n = j.days_onsite;
    w.append(el('small', '', (d.length > 1 ? 'to ' + fmtShortDay(d[d.length - 1]) + ' · ' : '') + (n ? n + (Number(n) === 1 ? ' day' : ' days') : 'days not set')));
    const what = el('div', 'what');
    const a = el('a', '', j.ref || j.quote); a.href = '#jobs';
    a.addEventListener('click', (e) => { e.preventDefault(); openJob(j.quote); });
    what.append(a, el('b', '', j.address), el('span', '', j.builder + ' · ' + (STATUS_LABEL[j.status || 'not-started'] || '')));
    const tag = el('span', 'tag', j.pm); tag.style.background = pmColour(j.pm);
    const cr = el('div');
    cr.className = 'cr ' + (isDone(j) ? '' : craneBooked(j) ? 'ok' : 'bad');
    cr.textContent = isDone(j) ? 'Completed' + (j.completed_date ? ' ' + fmtDate(j.completed_date) : '')
      : j.crane === 'Booked' ? 'Crane booked' : j.crane === 'Not needed' ? 'No crane needed' : 'Crane not booked';
    row.append(w, what, tag, cr); ag.append(row);
  });
}

export function initCalendar() {
  $('calPrev').addEventListener('click', () => { ui.cal.m--; if (ui.cal.m < 0) { ui.cal.m = 11; ui.cal.y--; } render(['calendar']); });
  $('calNext').addEventListener('click', () => { ui.cal.m++; if (ui.cal.m > 11) { ui.cal.m = 0; ui.cal.y++; } render(['calendar']); });
  $('calToday').addEventListener('click', () => { const t = today(); ui.cal.y = Number(t.slice(0, 4)); ui.cal.m = Number(t.slice(5, 7)) - 1; render(['calendar']); });
  registerView('calendar', renderCalendar);
}
